import React, { useEffect, useRef } from 'react';
import { computeSliceShift } from '../utils/sliceShift';
import { dbgCommitSeen, dbgEvent, dbgFps, dbgState, DBG_ENABLED, windowSummary, dbgWindowHeap } from '../utils/debugTelemetry';
import { jank, navMode } from '../utils/jankRecorder';
import { sync } from '../utils/syncProbe';
import { p1 } from '../utils/p1Probe';
import { side } from '../utils/sideProbe';
import { navRec } from '../utils/navRecorder';
import { swProbe } from '../utils/switchProbe';
import { msgProbe } from '../utils/msgProbe';
import { touchRec, installTouchListeners } from '../utils/touchRecorder';
import { DBG_CLEAN } from '../utils/dbgFlag';
import { useFlowaesStore } from '../store/useFlowaesStore';
import { positionFor, motionOf, type AnchoredNav } from '../utils/anchoredNav';

// ---------------------------------------------------------------------------
// Overlay de telemetria PASSIVA. Só é montado com "?dbg=1" (veja App.tsx).
//
// - Não usa setState: o texto é escrito direto no DOM (textContent) por um
//   setInterval de ~4 Hz, fora do ciclo de render do React e fora do useFrame.
// - Só LÊ refs/valores que o app já tem. Não altera navegação, escala, fetch,
//   clipping nem overscan.
// - Os cálculos (min/máx da janela visível etc.) rodam só aqui, dentro do
//   throttle de 250 ms.
// ---------------------------------------------------------------------------

export interface DebugOverlayProps {
  /** estado da navegação em índice ancorado (F1); targetHistoryOffset/historyOffsetRef são as vistas em distância do fim */
  nav?: AnchoredNav;
  targetHistoryOffset: React.MutableRefObject<number>;
  historyOffsetRef: React.MutableRefObject<number>;
  totalPoints: number;
  hasReachedHistoryEnd: boolean;
  isLoadingHistory: boolean;
  isReturningToLive: boolean;
  processedData: any[];
}

const TICK_MS = 250;
const WINDOW_10S = 10_000;

const f = (n: number | undefined, d = 1) => (n === undefined || !isFinite(n) ? '—' : n.toFixed(d));

function priceRange(data: any[], a: number, b: number, s: { showSMA10: boolean; showSMA20: boolean; showSMA50: boolean }) {
  let lo = Infinity, hi = -Infinity;
  for (let i = a; i <= b; i++) {
    const d = data[i];
    if (!d) continue;
    const v = d.close ?? d.price;
    if (v !== undefined && !isNaN(v)) { if (v < lo) lo = v; if (v > hi) hi = v; }
    if (s.showSMA10 && d.sma10 !== undefined && !isNaN(d.sma10)) { if (d.sma10 < lo) lo = d.sma10; if (d.sma10 > hi) hi = d.sma10; }
    if (s.showSMA20 && d.sma20 !== undefined && !isNaN(d.sma20)) { if (d.sma20 < lo) lo = d.sma20; if (d.sma20 > hi) hi = d.sma20; }
    if (s.showSMA50 && d.sma50 !== undefined && !isNaN(d.sma50)) { if (d.sma50 < lo) lo = d.sma50; if (d.sma50 > hi) hi = d.sma50; }
  }
  return { lo, hi, range: hi - lo };
}

export const DebugOverlay: React.FC<DebugOverlayProps> = (props) => {
  const propsRef = useRef(props);
  propsRef.current = props;
  const preRef = useRef<HTMLPreElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const hideBtnRef = useRef<HTMLButtonElement>(null);
  const textRef = useRef('');
  const buildFullRef = useRef<() => string>(() => '');
  const buildRef = useRef<() => string>(() => '');
  const markFilBtnRef = useRef<HTMLButtonElement>(null);
  const markSideBtnRef = useRef<HTMLButtonElement>(null);
  const longTasks = useRef<{ start: number; dur: number }[]>([]);
  const ltSupported = useRef(false);
  const autoplay = useRef<{ t0: number; hist0: number; max0: number; moved: boolean } | null>(null);

  // Dados novos já commitados: fecha a medição "setRealData -> commit" da última carga
  useEffect(() => { dbgCommitSeen(); }, [props.processedData]);

  // Long tasks da thread principal
  useEffect(() => {
    let obs: PerformanceObserver | null = null;
    try {
      if (typeof PerformanceObserver !== 'undefined' && (PerformanceObserver as any).supportedEntryTypes?.includes('longtask')) {
        obs = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) {
            longTasks.current.push({ start: e.startTime, dur: e.duration });
            if (longTasks.current.length > 300) longTasks.current.shift();
          }
        });
        obs.observe({ entryTypes: ['longtask'] });
        ltSupported.current = true;
      }
    } catch { ltSupported.current = false; }
    return () => { try { obs?.disconnect(); } catch { /* ok */ } };
  }, []);

  // Modo de navegação atual (0 parado, 1 arrasto manual, 2/3/4 autoplay 15/60/180). Só lê estado; sem alocação.
  const speedRef = useRef(0); // velocidade do histórico em candles/s, suavizada (média móvel exponencial)
  const currentMode = (): number => {
    const st = useFlowaesStore.getState();
    return navMode(st.activeGesture === 'time_drag', propsRef.current.isReturningToLive, st.returnSensitivity, speedRef.current);
  };
  const markBtnRef = useRef<HTMLButtonElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);

  // Gravador de quadros longos (travadinhas): um rAF, buffers pré-alocados, nada de setState
  useEffect(() => {
    const J = jank;
    if (!J) return;
    // velocidade pela coordenada de movimento (F2: vela nova ao vivo não conta como movimento)
    let id = 0, lastHeapT = 0, lastT = 0, lastHist = motionOf(propsRef.current.historyOffsetRef);
    const loop = () => {
      const t0 = performance.now();
      const hist = propsRef.current.historyOffsetRef.current, mv = motionOf(propsRef.current.historyOffsetRef);
      if (lastT > 0 && t0 > lastT) speedRef.current = speedRef.current * 0.9 + ((mv - lastHist) / (t0 - lastT)) * 1000 * 0.1;
      lastT = t0; lastHist = mv;
      const mode = currentMode();
      J.frame(t0, mode, hist);
      if (navRec) navRec.frame(t0, mode);   // P1b: dedo × posição, contra-arrasto, gravador de voo (antes da P1: os pulos usam o quadro)
      if (touchRec) touchRec.frame(t0, mode, useFlowaesStore.getState().activeGesture, navRec ? navRec.lastPed : 0); // P1c: estado do tratador e mediana do pedido por quadro de arrasto
      if (p1) p1.frame(t0, mode, mv); // P1: pulos no arrasto (Δ em velas), quadros e janela de carga
      if (t0 - lastHeapT >= 250) { // performance.memory aloca um objeto por leitura: só a cada 250 ms
        lastHeapT = t0;
        const mem = (performance as any).memory;
        if (mem && typeof mem.usedJSHeapSize === 'number') { J.heap(t0, mem.usedJSHeapSize, mode); dbgWindowHeap(mem.usedJSHeapSize / 1048576); }
      }
      J.overhead(performance.now() - t0);
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, []);

  // P1c: toques brutos (ouvinte passivo na captura da janela; só lê)
  useEffect(() => installTouchListeners(), []);

  // Long Animation Frames (Chrome 123+), se o navegador oferecer
  useEffect(() => {
    const J = jank;
    if (!J) return;
    let obs: PerformanceObserver | null = null;
    try {
      if (typeof PerformanceObserver !== 'undefined' && (PerformanceObserver as any).supportedEntryTypes?.includes('long-animation-frame')) {
        obs = new PerformanceObserver((list) => {
          for (const e of list.getEntries() as any[]) {
            const sc = e.scripts && e.scripts[0];
            const url = String(sc?.sourceURL || '');
            J.addLoaf({ t: e.startTime, dur: e.duration, block: e.blockingDuration ?? 0, url: url.slice(url.lastIndexOf('/') + 1).slice(0, 48), invoker: String(sc?.invoker || '').slice(0, 60), pos: sc?.sourceCharPosition ?? -1 });
          }
        });
        obs.observe({ type: 'long-animation-frame', buffered: false } as any);
        J.loafSupported = true;
      } else J.loafSupported = false;
    } catch { J.loafSupported = false; }
    return () => { try { obs?.disconnect(); } catch { /* ok */ } };
  }, []);

  // Botão "Marcar": registra tempo, modo, posição e FPS recente. Marca no pointerdown (menor atraso).
  const doMark = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (!jank) return;
    jank.mark(performance.now(), currentMode(), propsRef.current.historyOffsetRef.current);
    if (navRec) navRec.snap(performance.now(), 0); // P1b: gravador de voo
    if (markBtnRef.current) {
      markBtnRef.current.textContent = `Marcar (${jank.markN})`;
      markBtnRef.current.style.background = '#00d9ff'; markBtnRef.current.style.color = '#02040a';
      window.setTimeout(() => { if (markBtnRef.current) { markBtnRef.current.style.background = '#0a0e27'; markBtnRef.current.style.color = '#00d9ff'; } }, 160);
    }
    if (flashRef.current) {
      flashRef.current.style.opacity = '1';
      window.setTimeout(() => { if (flashRef.current) flashRef.current.style.opacity = '0'; }, 120);
    }
  };

  // Botão "Marcar filamento": o Mateus toca quando vê o filamento voltar a um estado anterior
  // "Marcar lateral": o Mateus toca quando vir a lateral piscar
  const doMarkSide = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (!side) return;
    side.mark(performance.now(), propsRef.current.historyOffsetRef.current);
    if (navRec) navRec.snap(performance.now(), 0); // P1b: gravador de voo
    if (markSideBtnRef.current) {
      markSideBtnRef.current.textContent = `Marcar lateral (${side.markN})`;
      markSideBtnRef.current.style.background = '#ff6ad5'; markSideBtnRef.current.style.color = '#02040a';
      window.setTimeout(() => { if (markSideBtnRef.current) { markSideBtnRef.current.style.background = '#0a0e27'; markSideBtnRef.current.style.color = '#ff6ad5'; } }, 160);
    }
  };

  const doMarkFil = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (!jank) return;
    jank.markFil(performance.now(), currentMode(), propsRef.current.historyOffsetRef.current);
    if (p1) p1.mark(performance.now(), currentMode(), propsRef.current.historyOffsetRef.current); // P1 T4
    if (navRec) navRec.snap(performance.now(), 0); // P1b: gravador de voo
    if (markFilBtnRef.current) {
      markFilBtnRef.current.textContent = `Marcar filamento (${jank.fmN})`;
      markFilBtnRef.current.style.background = '#ffd400'; markFilBtnRef.current.style.color = '#02040a';
      window.setTimeout(() => { if (markFilBtnRef.current) { markFilBtnRef.current.style.background = '#0a0e27'; markFilBtnRef.current.style.color = '#ffd400'; } }, 160);
    }
    if (flashRef.current) {
      flashRef.current.style.opacity = '1';
      window.setTimeout(() => { if (flashRef.current) flashRef.current.style.opacity = '0'; }, 120);
    }
  };

  // P1b T6(d): volta do segundo plano: marca e tira os 2 s seguintes das estatísticas (quadros longos, alarmes, FPS)
  useEffect(() => {
    if (!jank) return;
    const onVis = () => { if (document.visibilityState === 'visible') jank!.bgReturn(performance.now()); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // FPS: só conta quadros (requestAnimationFrame) e grava 1 amostra por segundo; sem setState
  useEffect(() => {
    let frames = 0, last = performance.now(), id = 0;
    const loop = (now: number) => {
      if (jank && now < jank.bgUntil) { frames = 0; last = now; id = requestAnimationFrame(loop); return; } // P1b T6(d)
      frames++;
      if (now - last >= 1000) { dbgFps(Math.round((frames * 1000) / (now - last))); frames = 0; last = now; }
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, []);

  // Início/fim do autoplay (retorno ao presente)
  useEffect(() => {
    const P = propsRef.current;
    const hist = P.historyOffsetRef.current;
    const max = Math.max(0, P.totalPoints - 1000);
    if (P.isReturningToLive) {
      autoplay.current = { t0: performance.now(), hist0: hist, max0: max, moved: false };
      dbgEvent(`autoplay INÍCIO: hist=${f(hist, 0)} max=${f(max, 0)} dívida=${f(hist - max, 0)}`);
    } else if (autoplay.current) {
      const a = autoplay.current;
      dbgEvent(`autoplay FIM após ${f((performance.now() - a.t0) / 1000, 1)}s: hist=${f(hist, 0)}${a.moved ? '' : ' (tela NÃO chegou a andar)'}`);
      autoplay.current = null;
    }
  }, [props.isReturningToLive]);

  // Tick de ~4 Hz: monta o texto e escreve direto no DOM
  useEffect(() => {
    const build = (): string => {
      const P = propsRef.current;
      const now = performance.now();
      const target = P.targetHistoryOffset.current;
      const hist = P.historyOffsetRef.current;
      const max = Math.max(0, P.totalPoints - 1000); // mesma conta de SceneContent/App
      const debt = target - max;

      // autoplay: quando a tela começa a andar (hist abaixo do que a janela ainda cobre)
      const ap = autoplay.current;
      if (ap && !ap.moved && hist <= max && hist < ap.hist0 - 1) {
        ap.moved = true;
        dbgEvent(`autoplay: tela começou a andar após ${f(now - ap.t0, 0)}ms (dívida inicial ${f(ap.hist0 - ap.max0, 0)}; resolução ${TICK_MS}ms)`);
      }

      const L: string[] = [];
      const fps10 = dbgState.fps.filter(x => now - x.t <= WINDOW_10S);
      const fpsNow = dbgState.fps[dbgState.fps.length - 1]?.fps;
      L.push(`FLOWAES DBG  t=${f((now - dbgState.t0) / 1000, 0)}s  FPS=${fpsNow ?? '—'} (10s: mín ${fps10.length ? Math.min(...fps10.map(x => x.fps)) : '—'} média ${fps10.length ? f(fps10.reduce((a, x) => a + x.fps, 0) / fps10.length, 0) : '—'})`);
      L.push('— NAVEGAÇÃO —');
      L.push(`target=${f(target, 1)}  hist(ref)=${f(hist, 1)}  Δ=${f(target - hist, 1)}`);
      L.push(`maxPossibleOffset=${f(max, 0)}  target−max=${f(debt, 0)} ${debt > 0 ? '<< DÍVIDA' : '(ok)'}`);
      const N = P.nav;
      if (N) L.push(`ancorado: alvo #${f(N.targetIndex, 1)}  pos #${f(N.positionIndex, 1)}  vela mais nova #${N.newestAbs}  limite #${N.limitAbs === null ? '—' : N.limitAbs}`);
      L.push(`total=${P.totalPoints}  fimHistórico=${P.hasReachedHistoryEnd ? 'SIM' : 'não'}  carregando=${P.isLoadingHistory ? 'SIM' : 'não'}  retornando=${P.isReturningToLive ? 'SIM' : 'não'}`);

      L.push('— CARGAS —');
      const loads = dbgState.loads;
      const lastMin = loads.filter(l => now - l.t <= 60_000).length;
      const last = loads[loads.length - 1];
      const lt = dbgState.loadTimes;
      const per1 = lt.filter(t => now - t <= 1000).length;
      const per5 = lt.filter(t => now - t <= 5000).length / 5;
      let peak = 0; { const b = new Map<number, number>(); for (const t of lt) if (now - t <= 60_000) { const k = Math.floor(t / 1000); b.set(k, (b.get(k) || 0) + 1); } for (const v of b.values()) peak = Math.max(peak, v); }
      L.push(`loadMoreHistory últimos 60s: ${lastMin}  (total na sessão: ${dbgState.loadSeq})`);
      L.push(`cargas/s: agora ${per1}  média 5s ${f(per5, 1)}  pico 60s ${peak}`);
      if (last) {
        L.push(`última #${last.id}${last.repeated ? ' REPETIDA' : ''}: ${last.failed ? `FALHA (${last.failed}), fim não marcado` : last.ended ? 'sem velas novas (' + (last.reason || 'fim') + ')' : `endTime=${last.endTime ? new Date(last.endTime).toISOString().slice(0, 16) : '?'} total=${last.total}`}`);
        if (!last.ended && !last.failed) L.push(`rede=${f(last.netMs, 0)}ms  proc(thread principal)=${f(last.procMs, 0)}ms  commit=${f(last.commitMs, 0)}ms  total=${f(last.totalMs, 0)}ms`);
      }

      L.push('— processMathData —');
      const m10 = dbgState.math.filter(c => now - c.t <= WINDOW_10S);
      const lastM = dbgState.math[dbgState.math.length - 1];
      const mMax = m10.reduce((a, c) => Math.max(a, c.ms), 0);
      const mAvg = m10.length ? m10.reduce((a, c) => a + c.ms, 0) / m10.length : 0;
      L.push(`roda na THREAD PRINCIPAL (sem Worker)`);
      L.push(`últimos 10s: ${m10.length} chamadas | média=${f(mAvg)}ms máx=${f(mMax)}ms | última=${lastM ? f(lastM.ms) + 'ms (' + lastM.n + ' velas)' : '—'}`);

      L.push('— LONG TASKS (thread principal) —');
      if (!ltSupported.current) {
        L.push('não suportado neste navegador (n/d)');
      } else {
        const lt10 = longTasks.current.filter(e => now - e.start <= WINDOW_10S);
        const sum = lt10.reduce((a, e) => a + e.dur, 0);
        const mx = lt10.reduce((a, e) => Math.max(a, e.dur), 0);
        L.push(`últimos 10s: ${lt10.length} tarefas >50ms | máx=${f(mx, 0)}ms soma=${f(sum, 0)}ms`);
      }

      if (sync) L.push(`dessincronia: alarmes ${sync.alarmN} | filamento velho (quadros) ${sync.staleF.reduce((a, c) => a + c, 0)} | candles velhos ${sync.staleC.reduce((a, c) => a + c, 0)} | |Δx| máx ${f(sync.maxAbsDx, 2)} vela | marcas de filamento ${jank ? jank.fmN : 0}`);
      if (side) L.push(`lateral: alarmes ${side.alarmN} | maior erro de borda ${f(side.maxBordaVelas, 1)} vela | marcas ${side.markN}`);
      if (jank) L.push(`travadinhas: >=25ms últimos 60s: ${jank.recentLong(now, 60_000)} | >=50ms: ${jank.recentLong(now, 60_000, 50)} | marcas: ${jank.markN} | GC provável: ${jank.gcCount}`);
      L.push('— ESCALA —');
      const S = dbgState.scene;
      if (!S || S.data.length === 0) {
        L.push('sem dados de cena ainda');
      } else {
        const shift = computeSliceShift(positionFor(P.historyOffsetRef, S.newestAbs ?? NaN), S.sliceOffset, S.padLeft, S.padRight);
        const len = S.data.length;
        const start = Math.min(len - 1, Math.max(0, Math.floor(S.padLeft - shift)));
        const end = Math.min(len - 1, start + S.visibleCount - 1);
        const buf = priceRange(S.data, 0, len - 1, S);
        const vis = priceRange(S.data, start, end, S);
        const domain = S.maxP - S.minP;
        L.push(`buffer: ${len} velas | domínio do hook minP=${f(S.minP, 2)} maxP=${f(S.maxP, 2)} faixa=${f(domain, 2)}`);
        L.push(`buffer bruto: ${f(buf.lo, 2)}..${f(buf.hi, 2)} faixa=${f(buf.range, 2)}`);
        L.push(`visível: idx ${start}..${end} (padLeft=${S.padLeft} smoothDiff=${f(shift, 1)}) ${f(vis.lo, 2)}..${f(vis.hi, 2)} faixa=${f(vis.range, 2)}`);
        L.push(`razão buffer/visível=${vis.range > 0 ? f(buf.range / vis.range, 2) : '—'}  visível ocupa ${domain > 0 ? f((vis.range / domain) * 100, 0) : '—'}% da altura`);
        L.push(`parede: minP=${f(S.wallMinP, 2)} maxP=${f(S.wallMaxP, 2)} ${S.wallStale ? '(DEFASADA vs filamento)' : '(em dia)'} lote parede=${S.wallSliceOffset}`);
        L.push(`último re-slice: ${dbgState.lastScaleJumpText}`);
        const sw = dbgState.scaleLog[dbgState.scaleLog.length - 1];
        L.push(sw ? `escala (10s): ocupação ${f(sw.occAvg, 0)}% | variação máx ${f(sw.stepMaxFrame)}%/quadro ${f(sw.stepMaxPerSec, 0)}%/s | ≥10%: ${sw.framesGe10} | corte: ${sw.cutFrames} | cálculo pico ${f(sw.calcMaxMs, 3)}ms` : 'escala (10s): aguardando primeira janela');
      }
      return L.join('\n');
    };

    const buildFull = (): string => {
      const head = [
        `FLOWAES DBG — log copiado em ${new Date().toISOString()}`,
        `UA: ${navigator.userAgent}`,
        `viewport: ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio}`,
        `url: ${window.location.origin}${window.location.pathname}?dbg=1`,
        ...(swProbe ? [...swProbe.report(dbgState.t0, 30), ''] : []),
        ...(msgProbe ? [...msgProbe.report(dbgState.t0, 40, performance.now()), ''] : []),
        ...(touchRec ? [...touchRec.jumpsReport(dbgState.t0, 100, 30), ...touchRec.buttonsReport(dbgState.t0, 60), '', ...touchRec.stateReport(dbgState.t0, 60), '', ...touchRec.rawReport(dbgState.t0, 120), ''] : []),
        ...(navRec ? [...navRec.counterReport(dbgState.t0, 20), ...navRec.writesReport()] : []),
        ...(p1 ? [...p1.jumpsReport(dbgState.t0, 15), ...p1.loadsReport(dbgState.t0, 20), ...p1.marksReport(dbgState.t0, 40), ...windowSummary(), ...p1.framesReport(), ...p1.stepsReport(dbgState.t0, 20), ''] : []),
        ...(navRec ? [...navRec.snapsReport(dbgState.t0, true), '', ...navRec.writesList(dbgState.t0, 200), ''] : []),
        '=== ESTADO ATUAL ===',
        DBG_CLEAN ? buildRef.current() : textRef.current,
        '',
        `=== CARGAS (últimas ${dbgState.loads.length}) ===`,
        ...dbgState.loads.map(l => `#${l.id} t=${f((l.t - dbgState.t0) / 1000, 1)}s ${l.failed ? `FALHA (${l.failed}), fim não marcado` : l.ended ? 'sem velas novas (' + (l.reason || 'fim') + ')' : `${l.repeated ? 'REPETIDA ' : ''}+${l.fetched} total=${l.total} endTime=${l.endTime ? new Date(l.endTime).toISOString() : '?'} rede=${f(l.netMs, 0)}ms proc=${f(l.procMs, 0)}ms commit=${f(l.commitMs, 0)}ms total=${f(l.totalMs, 0)}ms`}`),
        '',
        `=== processMathData (últimas ${dbgState.math.length} chamadas, thread principal) ===`,
        ...dbgState.math.map(c => `t=${f((c.t - dbgState.t0) / 1000, 1)}s ${f(c.ms)}ms ${c.n} velas`),
        '',
        `=== LONG TASKS (últimas ${longTasks.current.length}${ltSupported.current ? '' : ', não suportado'}) ===`,
        ...longTasks.current.slice(-40).map(e => `t=${f((e.start - dbgState.t0) / 1000, 1)}s ${f(e.dur, 0)}ms`),
        '',
        `=== FPS (1 amostra/s, últimas ${dbgState.fps.length}) ===`,
        dbgState.fps.map(x => `${f((x.t - dbgState.t0) / 1000, 0)}s:${x.fps}`).join(' '),
        '',
        `=== CARGAS/S (por segundo, últimos 60 s) ===`,
        (() => { const b = new Map<number, number>(); const n = performance.now(); for (const t of dbgState.loadTimes) if (n - t <= 60_000) { const k = Math.floor((t - dbgState.t0) / 1000); b.set(k, (b.get(k) || 0) + 1); } return [...b.entries()].map(([k, v]) => `${k}s:${v}`).join(' ') || '—'; })(),
        '',
        `=== ESCALA (janelas de 10 s, últimas ${dbgState.scaleLog.length}) ===`,
        ...dbgState.scaleLog.map(w => `t=${f((w.t - dbgState.t0) / 1000, 0)}s quadros=${w.frames} ocupação=${f(w.occAvg, 0)}% (mín ${f(w.occMin, 0)}%) variação=${f(w.stepMaxFrame)}%/quadro ${f(w.stepMaxPerSec, 0)}%/s ≥10%=${w.framesGe10} corte=${w.cutFrames} cálculo=${w.calcN}x média ${f(w.calcAvgMs, 3)}ms pico ${f(w.calcMaxMs, 3)}ms`),
        '',
        ...(jank ? [...jank.report(performance.now(), dbgState.t0), ''] : []),
        ...(sync ? [...sync.report(dbgState.t0, DBG_CLEAN), ''] : []),
        ...(side ? [...side.report(dbgState.t0, false), ''] : []),
        `=== EVENTOS (últimos ${dbgState.events.length}) ===`,
        ...dbgState.events.map(e => `t=${f((e.t - dbgState.t0) / 1000, 1)}s ${e.text}`),
      ];
      return head.join('\n');
    };
    buildFullRef.current = buildFull;
    buildRef.current = build;

    // Modo limpo (?dbg=1&clean=1): nenhuma atualização de DOM por setInterval; o log é montado só ao copiar
    if (DBG_CLEAN) return;
    const tick = () => {
      const txt = build();
      textRef.current = txt;
      if (preRef.current) preRef.current.textContent = txt;
    };
    tick();
    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const summaryBtnRef = useRef<HTMLButtonElement>(null);

  // "Copiar resumo": texto curto (<= 6 KB) para o log não chegar cortado. Sem as listas longas.
  const buildSummary = (): string => {
    const MAX = 6000;
    const hasLine = (l: string) => !/^t=/.test(l) && !/^LEGENDA/.test(l) && l.trim() !== '';
    // mantém só as seções permitidas; dentro delas, descarta itens de lista (t=...) e limita linhas
    const pick = (lines: string[], allow: RegExp[], perSection = 12): string[] => {
      const out: string[] = []; let on = false, cnt = 0;
      for (const l of lines) {
        if (/^(=== |--- )/.test(l)) { on = allow.some(r => r.test(l)); cnt = 0; if (on) out.push(l.length > 170 ? l.slice(0, 170) + '…' : l); continue; }
        if (!on || !hasLine(l) || cnt >= perSection) continue;
        cnt++; out.push(l.length > 230 ? l.slice(0, 230) + '…' : l);
      }
      return out;
    };
    const stateLines = (buildRef.current() || '').split('\n').slice(0, 30).map(l => (l.length > 170 ? l.slice(0, 170) + '…' : l));
    const ev = dbgState.events.filter(e => /pré-carga|gesto:|tela parada|re-âncora|RECUO|limite:|FIM DO HISTÓRICO|carga de histórico descartada|janela:|costura|mais novas|FALHA/.test(e.text)).slice(-24);
    const parts: string[] = [
      `FLOWAES DBG — RESUMO copiado em ${new Date().toISOString()}`,
      `viewport ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio} | ${navigator.userAgent.slice(0, 90)}`,
      // no começo, o que mais importa (o resumo é cortado no fim se passar de 6 KB). P1: pulos, travamento por carga,
      // marcas, janela de velas (F3a), quadro em arrasto e degraus de escala; depois os alarmes de dessincronia e a LATERAL
      // P1b: CONTRA-ARRASTO no topo; depois pulos, travamento por carga, marcas, janela, quadro e degraus
      // F3c: TROCAS DE SÉRIE no topo (últimas 10)
      ...(swProbe ? ['', ...swProbe.report(dbgState.t0, 10)] : []),
      // F3e C6: MENSAGENS de histórico (últimas 6)
      ...(msgProbe ? ['', ...msgProbe.report(dbgState.t0, 6, performance.now())] : []),
      // P1c: SALTOS DO ARRASTO no topo
      ...(touchRec ? ['', ...touchRec.jumpsReport(dbgState.t0, 3, 6)] : []),
      ...(navRec ? ['', ...navRec.counterReport(dbgState.t0, 3), ...navRec.writesReport()] : []),
      ...(p1 ? ['', ...p1.jumpsReport(dbgState.t0, 3), '', ...p1.loadsReport(dbgState.t0, 4), '', ...p1.marksReport(dbgState.t0, 6)] : []),
      '', ...windowSummary(),
      ...(p1 ? ['', ...p1.framesReport(), '', ...p1.stepsReport(dbgState.t0, 4)] : []),
      ...(navRec ? ['', ...navRec.snapsReport(dbgState.t0, false)] : []),
      '', ...(sync ? sync.summaryTop(dbgState.t0) : []),
      '', ...(side ? side.report(dbgState.t0, true) : []),
      '', '=== ESTADO ===', ...stateLines,
      '', `=== EVENTOS NOVOS (últimos ${ev.length}) ===`, ...ev.map(e => `t=${f((e.t - dbgState.t0) / 1000, 1)}s ${e.text}`),
      '', ...(jank ? pick(jank.report(performance.now(), dbgState.t0), [/TRAVADINHAS/, /\(b\)/, /\(c\)/, /\(d\)/], 4) : []),
      '', ...(sync ? pick(sync.report(dbgState.t0, DBG_CLEAN), [/DESSINCRONIA/, /por modo: quadros/, /causas dos quadros/, /repintura/, /marcas de filamento/], 10) : []),
    ];
    let txt = parts.join('\n');
    // limite em BYTES (acentos ocupam 2 em UTF-8): o texto colado não pode passar de 6 KB
    const enc = new TextEncoder(), tail = '\n… (cortado em 6 KB)';
    if (enc.encode(txt).length > MAX) {
      let n = MAX - 40;
      while (n > 0 && enc.encode(txt.slice(0, n)).length > MAX - 40) n -= 40;
      txt = txt.slice(0, n) + tail;
    }
    return txt;
  };

  const copyText = async (text: string, btn: React.RefObject<HTMLButtonElement>, label: string) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (btn.current) {
      btn.current.textContent = ok ? 'Copiado!' : 'Falhou';
      window.setTimeout(() => { if (btn.current) btn.current.textContent = label; }, 1500);
    }
  };
  const copySummary = () => copyText(buildSummary(), summaryBtnRef, 'Copiar resumo');

  const copyLog = async () => {
    const full = buildFullRef.current() || textRef.current;
    await copyText(full, btnRef, 'Copiar log');
  };

  const toggle = () => {
    if (!preRef.current || !hideBtnRef.current) return;
    const hidden = preRef.current.style.display === 'none';
    preRef.current.style.display = hidden ? 'block' : 'none';
    hideBtnRef.current.textContent = hidden ? 'Ocultar' : 'Mostrar';
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const btnStyle: React.CSSProperties = {
    pointerEvents: 'auto', font: '600 10px monospace', padding: '4px 8px', marginRight: 4,
    background: '#0a0e27', color: '#00d9ff', border: '1px solid #00d9ff66', borderRadius: 4,
  };

  if (!DBG_ENABLED) return null;
  return (
    <div
      data-testid="dbg-overlay"
      style={{ position: 'fixed', left: 4, bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4px)', zIndex: 2147483000, pointerEvents: 'none', maxWidth: '94vw' }}
    >
      <div>
        <button ref={btnRef} style={btnStyle} onPointerDown={stop} onPointerUp={stop} onTouchStart={stop} onTouchEnd={stop} onClick={copyLog}>Copiar log</button>
        <button ref={summaryBtnRef} style={btnStyle} onPointerDown={stop} onPointerUp={stop} onTouchStart={stop} onTouchEnd={stop} onClick={copySummary}>Copiar resumo</button>
        <button ref={markBtnRef} style={{ ...btnStyle, padding: '12px 18px', font: '700 13px monospace', touchAction: 'manipulation', userSelect: 'none' }} onPointerDown={doMark} onPointerUp={stop} onTouchStart={stop} onTouchEnd={stop} onClick={stop}>Marcar</button>
        <button ref={markFilBtnRef} style={{ ...btnStyle, padding: '12px 18px', font: '700 13px monospace', color: '#ffd400', border: '1px solid #ffd40088', touchAction: 'manipulation', userSelect: 'none' }} onPointerDown={doMarkFil} onPointerUp={stop} onTouchStart={stop} onTouchEnd={stop} onClick={stop}>Marcar filamento</button>
        <button ref={markSideBtnRef} style={{ ...btnStyle, padding: '12px 18px', font: '700 13px monospace', color: '#ff6ad5', border: '1px solid #ff6ad588', touchAction: 'manipulation', userSelect: 'none' }} onPointerDown={doMarkSide} onPointerUp={stop} onTouchStart={stop} onTouchEnd={stop} onClick={stop}>Marcar lateral</button>
        <button ref={hideBtnRef} style={btnStyle} onPointerDown={stop} onPointerUp={stop} onTouchStart={stop} onTouchEnd={stop} onClick={toggle}>Ocultar</button>
      </div>
      <div ref={flashRef} style={{ position: 'fixed', inset: 0, border: '3px solid #00d9ff', opacity: 0, transition: 'opacity 120ms', pointerEvents: 'none' }} />
      {DBG_CLEAN && <div style={{ font: '700 10px monospace', color: '#ffd400', pointerEvents: 'none' }}>MODO LIMPO (sem atualização de tela do diagnóstico; toque em Copiar log no fim)</div>}
      <pre
        ref={preRef}
        style={{ display: DBG_CLEAN ? 'none' : undefined, margin: '4px 0 0', padding: 6, font: '9px/1.2 monospace', color: '#b6f7ff', background: 'rgba(2,4,10,0.78)', border: '1px solid #00d9ff33', borderRadius: 4, maxHeight: '52vh', overflow: 'hidden', whiteSpace: 'pre-wrap', pointerEvents: 'none' }}
      />
    </div>
  );
};

export default DebugOverlay;
